import React from 'react';

import {
  DescriptionItem,
  DescriptionList,
  Section,
  SidePage,
  Tags,
  Typography,
} from '@gogame/components';
import { useChildComponent } from '@gogame/hooks-ts';

import {
  useLoadConversionPageDetails,
  useIsUpdating,
} from 'scenes/Gallery/ConversionPages/ConversionPages/data';
import ConversionGroupPreviewScreenLink from 'scenes/Gallery/ConversionPages/ConversionGroups/ConversionGroupPreviewScreenLink';

import { DIMENSION_REAL_CONVERSION_PAGE } from 'scenes/Trackers/Tracker/constants';

import { PreviewFile } from 'scenes/Gallery/components/File';
import {
  AttributeItem,
  AttributeList,
} from 'scenes/Gallery/components/AttributeList';
import AdsLink from 'scenes/Gallery/ConversionPages/ConversionPages/AdsLink';

import ConversionPageFormEdit from 'scenes/Gallery/ConversionPages/ConversionPages/ConversionPageForm';
import ConversionPageActions from 'scenes/Gallery/ConversionPages/ConversionPages/Actions';

const ConversionPageDetails = ({ id, onClose, onExited, open }) => {
  const [childComponent, openChildComponent] = useChildComponent();
  const { data, isLoading, isRefetching } = useLoadConversionPageDetails({
    id,
  });
  const isUpdating = useIsUpdating();

  const handleEdit = ({ id }) => {
    openChildComponent(ConversionPageFormEdit, { id });
  };

  return (
    <SidePage
      backdrop={false}
      onClose={onClose}
      onExited={onExited}
      loading={isLoading || isUpdating}
      fetching={isRefetching}
      open={open}
      size={'lg'}
    >
      <SidePage.Title
        title={'Conversion Page'}
        subTitle={data?.name}
        entityId={id}
      />
      <SidePage.NewBody wrapper={true}>
        <SidePage.NewContent withSidebar={true}>
          {data ? (
            <PreviewFile src={data.preview} width={'auto'} height={375} />
          ) : null}
          <Section
            title={'General'}
            action={
              <AttributeList variant={'outlined'} typographyVariant={'caption'}>
                {data?.usedInAds ? (
                  <AdsLink
                    dimension={DIMENSION_REAL_CONVERSION_PAGE}
                    id={data.name}
                  >
                    <AttributeItem
                      valueParser={'ads'}
                      clickable={true}
                      underline={'always'}
                    />
                  </AdsLink>
                ) : null}
              </AttributeList>
            }
          >
            <DescriptionList
              stripEmpty={true}
              data={data}
              disableGutters={true}
            >
              <DescriptionItem
                label={'Group'}
                field={'group'}
                type={({ value }) =>
                  value ? (
                    <Typography>
                      <ConversionGroupPreviewScreenLink id={value.id}>
                        {value.name}
                      </ConversionGroupPreviewScreenLink>
                    </Typography>
                  ) : null
                }
              />
              <DescriptionItem
                label={'Application'}
                field={'application.name'}
                type={'text'}
              />
              <DescriptionItem
                label={'Domain'}
                field={'domain'}
                type={'text'}
              />
              <DescriptionItem
                label={'Locale'}
                field={'locale'}
                type={'text'}
              />
              <DescriptionItem
                label={'Flow'}
                field={'conversionPageFlow.name'}
                type={'text'}
              />
              <DescriptionItem
                label={'Platform'}
                field={'platform.name'}
                type={'text'}
              />
              <DescriptionItem
                label={'Tags'}
                field={'tags'}
                type={({ value }) => (
                  <Tags
                    data={value}
                    getTagLabel={({ name }) => name}
                    lineClamp={4}
                  />
                )}
              />
            </DescriptionList>
          </Section>
        </SidePage.NewContent>
        <SidePage.Sidebar
          actions={
            <ConversionPageActions limit={3} data={data} onEdit={handleEdit} />
          }
        >
          <DescriptionList
            variant={'column'}
            data={data}
            stripEmpty={true}
            disableGutters={true}
          >
            <DescriptionItem
              label={'Last updated by'}
              type={'metadata'}
              value={data}
              userField={'lastUpdatedBy'}
              dateField={'lastUpdatedDate'}
            />
            <DescriptionItem
              label={'Created'}
              type={'metadata'}
              value={data}
              userField={'createdBy'}
              dateField={'createdDate'}
            />
          </DescriptionList>
        </SidePage.Sidebar>
      </SidePage.NewBody>
      <SidePage.Actions>
        <SidePage.CloseButton />
      </SidePage.Actions>
      {childComponent}
    </SidePage>
  );
};

export default ConversionPageDetails;
